import type { QueryClient } from '@tanstack/react-query';
import type { BranchListParams } from '../services/branchService';
import type { ReviewListParams } from '../services/reviewService';

// Namespaced root keys for library surfaces — match the `.all` arrays
// declared on `categoryKeys` / `subcategoryKeys` (usePublishedContent.ts)
// and `contentKeys` (useContent.ts). Inlined here rather than imported
// to avoid a circular dep — those hook files already import from here.
const CATEGORIES_ROOT = ['categories'] as const;
const SUBCATEGORIES_ROOT = ['subcategories'] as const;
const CONTENTS_ROOT = ['contents'] as const;

// Branch query keys
export const branchKeys = {
  all: ['branches'] as const,
  lists: () => [...branchKeys.all, 'list'] as const,
  list: (params: BranchListParams) => [...branchKeys.lists(), params] as const,
  details: () => [...branchKeys.all, 'detail'] as const,
  detail: (id: string) => [...branchKeys.details(), id] as const,
  myBranches: () => [...branchKeys.all, 'my'] as const,
  reviewBranches: () => [...branchKeys.all, 'reviews'] as const,
};

// Search query keys (spec 021 — library search)
export const searchKeys = {
  all: ['search'] as const,
  byQuery: (q: string, limit: number) =>
    [...searchKeys.all, q.toLowerCase().trim(), limit] as const,
};

// Review query keys
export const reviewKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewKeys.all, 'list'] as const,
  list: (params: ReviewListParams) => [...reviewKeys.lists(), params] as const,
  details: () => [...reviewKeys.all, 'detail'] as const,
  detail: (id: string) => [...reviewKeys.details(), id] as const,
  myReviews: () => [...reviewKeys.all, 'my'] as const,
  branchReviews: (branchId: string) =>
    [...reviewKeys.all, 'branch', branchId] as const,
  branchStats: (branchId: string) =>
    [...reviewKeys.all, 'branch', branchId, 'stats'] as const,
  comments: (id: string) => [...reviewKeys.all, id, 'comments'] as const,
};

/**
 * Invalidate all dashboard-related queries after any workflow transition.
 * Call this from every mutation that changes branch state or review status.
 *
 * Also invalidates categories / subcategories / published content. Branch
 * publish promotes branch-scoped categories to main and pushes branch
 * content into the published surface — both are sidebar-visible. Without
 * busting these caches the sidebar shows stale data (e.g. a freshly
 * promoted private category appears as missing) until the 5-minute
 * staleTime expires or the user hard-refreshes.
 */
export function invalidateWorkflowQueries(
  queryClient: QueryClient,
  branchId?: string
) {
  // Workflow lists
  queryClient.invalidateQueries({ queryKey: branchKeys.myBranches() });
  queryClient.invalidateQueries({ queryKey: branchKeys.reviewBranches() });
  queryClient.invalidateQueries({ queryKey: reviewKeys.myReviews() });
  queryClient.invalidateQueries({ queryKey: branchKeys.lists() });
  if (branchId) {
    queryClient.invalidateQueries({ queryKey: branchKeys.detail(branchId) });
    queryClient.invalidateQueries({ queryKey: reviewKeys.branchReviews(branchId) });
    queryClient.invalidateQueries({ queryKey: reviewKeys.branchStats(branchId) });
  }

  // Library surfaces affected by publish/archive: sidebar categories,
  // their subcategories, and the published content list.
  queryClient.invalidateQueries({ queryKey: CATEGORIES_ROOT });
  queryClient.invalidateQueries({ queryKey: SUBCATEGORIES_ROOT });
  queryClient.invalidateQueries({ queryKey: CONTENTS_ROOT });
}
